import React, { useEffect, useState, useRef, useCallback } from 'react';
import './Toast.css';

// Matches the .toast transition duration in Toast.css (0.3s).
const TOAST_EXIT_TRANSITION_MS = 300;

export interface ToastProps {
    message: string;
    type: 'success' | 'error' | 'info';
    duration?: number;
    onClose: () => void;
}

export const Toast: React.FC<ToastProps> = ({
    message,
    type,
    duration = 4000,
    onClose
}) => {
    const [isVisible, setIsVisible] = useState(true);
    const isDismissingRef = useRef(false);
    const dismissTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);
    const autoDismissTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);

    // Unified dismissal handler - ensures onClose is called exactly once
    const handleDismiss = useCallback(() => {
        if (isDismissingRef.current) {
            return;
        }

        isDismissingRef.current = true;

        // Clear any pending auto-dismiss timer
        if (autoDismissTimerRef.current) {
            clearTimeout(autoDismissTimerRef.current);
        }

        // Trigger exit animation
        setIsVisible(false);

        // Wait for animation to complete, then call onClose
        dismissTimerRef.current = setTimeout(() => {
            onClose();
        }, TOAST_EXIT_TRANSITION_MS);
    }, [onClose]);

    useEffect(() => {
        // Auto-dismiss after duration
        autoDismissTimerRef.current = setTimeout(() => {
            handleDismiss();
        }, duration);

        // Cleanup on unmount
        return () => {
            if (autoDismissTimerRef.current) {
                clearTimeout(autoDismissTimerRef.current);
            }
            if (dismissTimerRef.current) {
                clearTimeout(dismissTimerRef.current);
            }
        };
    }, [duration, handleDismiss]);

    return (
        <div className="toast-container">
            <div
                className={`toast toast--${type} ${isVisible ? 'toast--visible' : 'toast--hidden'}`}
                data-testid="toast-container"
            >
                <div className="toast__content" data-testid={`toast-${type}`}>
                    <span className="toast__icon">
                        {type === 'success' && '✅'}
                        {type === 'error' && '❌'}
                        {type === 'info' && 'ℹ️'}
                    </span>
                    <span className="toast__message">{message}</span>
                    <button
                        className="toast__close"
                        onClick={handleDismiss}
                        aria-label="Close notification"
                    >
                        ×
                    </button>
                </div>
            </div>
        </div>
    );
};
